import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { Flex, InputRadio, useFormContext } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { type InformationGatheringAddress, normalizeAddress } from '@feature/information-gathering/information-gathering.model';
import { AddressFields } from './address-fields';
import { InformationGatheringForm } from './information-gathering-form';
import { useInformationGatheringJourney } from './use-information-gathering-journey';

const HOME_ADDRESS_FIELD_NAMES = {
	addressLine1: 'addressLine1',
	addressLine2: 'addressLine2',
	townOrCity: 'townOrCity',
	postcode: 'postcode',
} as const;

const CURRENT_LOCATION_FIELD_NAMES = {
	addressLine1: 'currentAddressLine1',
	addressLine2: 'currentAddressLine2',
	townOrCity: 'currentTownOrCity',
	postcode: 'currentPostcode',
} as const;

interface AddressFieldNames {
	addressLine1: string;
	addressLine2: string;
	townOrCity: string;
	postcode: string;
}

type AddressFormData<TNames extends AddressFieldNames> = Record<string, string> & Record<TNames[keyof AddressFieldNames], string>;

type LocationFormData = AddressFormData<typeof CURRENT_LOCATION_FIELD_NAMES> &
	AddressFormData<typeof HOME_ADDRESS_FIELD_NAMES> & {
		currentLocationIsHomeAddress: 'yes' | 'no' | '';
	};

function addressFromForm<TNames extends AddressFieldNames>(data: AddressFormData<TNames>, names: TNames): InformationGatheringAddress {
	return normalizeAddress({
		addressLine1: data[names.addressLine1],
		addressLine2: data[names.addressLine2],
		townOrCity: data[names.townOrCity],
		postcode: data[names.postcode],
	});
}

export function LocationScreen({ patientWording }: { patientWording: boolean }) {
	const { draft, saveDraft, complete, goBack } = useInformationGatheringJourney();
	const patient = draft.patient;

	function currentLocationIsHomeAddressValue(): LocationFormData['currentLocationIsHomeAddress'] {
		if (patient.currentLocationIsHomeAddress === null) return '';
		return patient.currentLocationIsHomeAddress ? 'yes' : 'no';
	}

	async function submit(data: LocationFormData) {
		const currentLocationIsHomeAddress = data.currentLocationIsHomeAddress === 'yes';
		const completedDraft = {
			...draft,
			patient: {
				...patient,
				currentLocation: addressFromForm(data, CURRENT_LOCATION_FIELD_NAMES),
				currentLocationIsHomeAddress,
				homeAddress: currentLocationIsHomeAddress ? patient.homeAddress : addressFromForm(data, HOME_ADDRESS_FIELD_NAMES),
			},
		};
		saveDraft(completedDraft);
		await complete(completedDraft);
	}

	return (
		<InformationGatheringForm
			title={
				patientWording
					? 'View.CreateNewMessage.InformationGathering.PatientLocation.Title'
					: 'View.CreateNewMessage.InformationGathering.Location.Title'
			}
			description={
				patientWording
					? 'View.CreateNewMessage.InformationGathering.PatientLocation.Description'
					: 'View.CreateNewMessage.InformationGathering.Location.Description'
			}
			defaultValues={{
				currentAddressLine1: patient.currentLocation.addressLine1,
				currentAddressLine2: patient.currentLocation.addressLine2,
				currentTownOrCity: patient.currentLocation.townOrCity,
				currentPostcode: patient.currentLocation.postcode,
				currentLocationIsHomeAddress: currentLocationIsHomeAddressValue(),
				...patient.homeAddress,
			}}
			onSubmit={submit}
			goBack={goBack}
		>
			<AddressFields names={CURRENT_LOCATION_FIELD_NAMES} patientWording={patientWording} currentLocation />
			<HomeAddressFields patientWording={patientWording} />
		</InformationGatheringForm>
	);
}

function HomeAddressFields({ patientWording }: { patientWording: boolean }) {
	const formContext = useFormContext();
	const currentLocationIsHomeAddress = formContext?.control.watch('currentLocationIsHomeAddress');

	return (
		<>
			<InputRadio.Root
				label={withTranslation(
					patientWording
						? 'View.CreateNewMessage.InformationGathering.PatientContact.AtHome'
						: 'View.CreateNewMessage.InformationGathering.SelfContact.AtHome',
				)}
				name='currentLocationIsHomeAddress'
				required
				options={{ required: I18nAndL10n.translate('Form.ValidationError.OnlyOneOption') }}
			>
				<InputRadio.Item label={withTranslation('Generic.Yes')} value='yes' />
				<InputRadio.Item label={withTranslation('Generic.No')} value='no' />
			</InputRadio.Root>
			{currentLocationIsHomeAddress === 'no' ? (
				<Flex role='region' aria-live='polite' gap={tokens.spacing.x1_5}>
					<AddressFields names={HOME_ADDRESS_FIELD_NAMES} patientWording={patientWording} />
				</Flex>
			) : null}
		</>
	);
}
